/**
 * HomeMusicAdmin — set/update the site-wide music player iframe URL.
 * The player is mounted at the App root as a floating bubble that expands
 * into a full embed panel; setting an empty URL hides the bubble entirely.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const HomeMusicAdmin = () => {
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/home-music`);
      setUrl(r.data?.url || '');
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (nextUrl) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/zona-pro/home-music`,
        { url: nextUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(nextUrl ? 'Reproductor actualitzat' : 'Reproductor amagat');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm('Vols amagar el reproductor a totes les pàgines?')) return;
    setUrl('');
    await save('');
  };

  return (
    <div className="space-y-4" data-testid="home-music-admin">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-lg">🎵 Reproductor de música (tot el site)</h3>
            <p className="text-sm text-slate-600 mt-1">
              Enganxa l&apos;URL <em>src</em> de l&apos;iframe (per exemple, la del
              player de Content-OS). Es mostrarà com una bombolla flotant a la
              cantonada inferior dreta a <strong>totes</strong> les pàgines.
              Si el deixes buit, el botó desapareix.
            </p>
          </div>

          <div>
            <Label className="text-xs">URL de l&apos;iframe</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://media-forge-249.emergent.host/embed/player?u=…"
              data-testid="home-music-url-input"
            />
            {loading && <div className="text-xs text-slate-400 mt-1">Carregant…</div>}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => save(url)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="home-music-save"
            >
              {saving ? 'Guardant…' : 'Guardar'}
            </Button>
            {url && (
              <Button
                variant="outline"
                onClick={clear}
                disabled={saving}
                className="text-red-600"
                data-testid="home-music-clear"
              >
                Amagar reproductor
              </Button>
            )}
          </div>

          {url && (
            <div className="mt-4">
              <Label className="text-xs">Previsualització</Label>
              <div className="mt-1 rounded-lg overflow-hidden border">
                <iframe
                  title="music-preview"
                  src={url}
                  loading="lazy"
                  className="w-full border-0"
                  style={{ height: '520px' }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeMusicAdmin;
